/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { jsx } from '@compiled/react';
import noop from 'lodash/noop';
import type { OptionsOrGroups, GroupBase } from '@atlaskit/react-select';
import { ButtonItem, Section } from '@atlaskit/menu';
import { useIntl } from '@atlassian/jira-intl';
import { useRouterActions } from '@atlassian/react-resource-router';
import { getProjectSettingsIssueTypesUrl } from '@atlassian/jira-issue-type-config-common/src/getProjectSettingsIssueTypesUrl.tsx';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import { UFOExperience, ExperienceTypes, ExperiencePerformanceTypes } from '@atlassian/ufo';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { RuntimePropsOfEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { asyncEditIssueTypeModalEntrypoint } from '@atlassian/jira-edit-issue-type-modal/entrypoint.tsx';
import {
	UNKNOWN_SOURCE,
	type ConfigureSource,
} from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { FormDialogSkeleton } from '@atlassian/jira-form-dialog-skeleton/src/ui/FormDialogSkeleton.tsx';
import type { UpdatedIssueType } from '@atlassian/jira-issue-type-config-controllers/src/controllers/use-update-issue-type/index.tsx';
import type { CreatedIssueType } from '@atlassian/jira-issue-type-config-controllers/src/controllers/create-issue-type/useCreateIssueType.tsx';
import type { Option } from '../types.tsx';
import messages from './messages.tsx';

export type FooterProps = {
	projectKey: string;
	projectType?: string | null;
	projectId: string;
	selectedIssueTypeId: string;
	allowIssueTypeCreation?: boolean;
	// Remove optional .? when cleaning up work_type_inline_config_nin_icc_fg flag
	onIssueTypeUpdated: (updatedIssueType?: UpdatedIssueType) => void;
	onIssueTypeCreated: (createdIssueType?: CreatedIssueType) => void;
	menuIsOpen: boolean;
	source?: ConfigureSource;
	setIsManageIssueTypesModalOpen?: (isOpen: boolean) => void;
	isSimplifiedProject?: boolean;
	options: OptionsOrGroups<Option, GroupBase<Option>>;
};

type CreateRuntimeProps = RuntimePropsOfEntryPoint<typeof asyncCreateIssueTypeModalEntryPoint>;
type EditRuntimeProps = RuntimePropsOfEntryPoint<typeof asyncEditIssueTypeModalEntrypoint>;

const getIssueTypeEditExperience = (source?: ConfigureSource) => {
	return new UFOExperience(`${source ?? UNKNOWN_SOURCE}.edit-issue-type`, {
		type: ExperienceTypes.Operation,
		performanceType: ExperiencePerformanceTypes.Custom,
	});
};

export const getRelevantIssueTypeIds = (options: OptionsOrGroups<Option, GroupBase<Option>>) => {
	return options
		.filter(
			(option): option is Option & { issueTypeId: string } =>
				'issueTypeId' in option && typeof option.issueTypeId === 'string',
		)
		.map((option) => option.issueTypeId);
};

export const MenuFooter = (props: FooterProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const cloudId = useCloudId();

	const {
		projectKey,
		projectType,
		projectId,
		selectedIssueTypeId,
		allowIssueTypeCreation = true,
		onIssueTypeUpdated,
		onIssueTypeCreated,
		source,
		setIsManageIssueTypesModalOpen,
		options,
		menuIsOpen,
		isSimplifiedProject,
	} = props;

	const projectSettingsUrl = getProjectSettingsIssueTypesUrl({
		projectKey,
		projectType: projectType ?? '',
		isSimplifiedProject,
	});

	const handleManageIssueTypesClick = useCallback(() => {
		push(projectSettingsUrl);
	}, [push, projectSettingsUrl]);

	const handleAddIssueTypeClick = useCallback(() => {
		setIsManageIssueTypesModalOpen?.(true);
		// TODO:  Add analytics
	}, [setIsManageIssueTypesModalOpen]);

	const handleEditIssueTypeClick = useCallback(() => {
		setIsManageIssueTypesModalOpen?.(true);
		// TODO:  Add analytics
	}, [setIsManageIssueTypesModalOpen]);

	const ufoExperience = getIssueTypeEditExperience(source);

	const createEntryPointProps: CreateRuntimeProps = useMemo(
		() => ({
			projectId: Number(projectId),
			projectKey,
			onClose: noop,
			onOpen: handleAddIssueTypeClick,
			onOpenManageTypes: noop,
			issueTypeId: selectedIssueTypeId,
			ufoExperience,
			onIssueTypeCreated: onIssueTypeCreated ?? noop,
			source: source ?? UNKNOWN_SOURCE,
			showNewIssueTypeForm: true,
			isProjectAdmin: true,
			isWrappedInModal: true,
		}),
		[
			projectId,
			projectKey,
			handleAddIssueTypeClick,
			selectedIssueTypeId,
			ufoExperience,
			onIssueTypeCreated,
			source,
		],
	);

	const editEntryPointProps: EditRuntimeProps = useMemo(
		() => ({
			projectId: Number(projectId),
			projectKey,
			onClose: noop,
			onOpen: handleEditIssueTypeClick,
			issueTypeId: selectedIssueTypeId,
			ufoExperience,
			onRefetchIssueTypesWhenIssueTypeUpdated: onIssueTypeUpdated ?? noop,
			calledFrom: source ?? UNKNOWN_SOURCE,
			shouldShowIssueTypeSelect: true,
			relevantIssueTypeIds: getRelevantIssueTypeIds(options),
		}),
		[
			projectId,
			projectKey,
			handleEditIssueTypeClick,
			selectedIssueTypeId,
			ufoExperience,
			onIssueTypeUpdated,
			source,
			options,
		],
	);

	const entryPointParams = useMemo(
		() => ({
			projectKey,
			cloudId,
		}),
		[projectKey, cloudId],
	);

	const modalProps = useMemo(
		() => ({
			...defaultModalProps,
			onCloseComplete: () => {
				setIsManageIssueTypesModalOpen?.(false);
			},
		}),
		[setIsManageIssueTypesModalOpen],
	);

	if (!menuIsOpen) {
		return null;
	}

	return (
		<Section hasSeparator>
			{allowIssueTypeCreation && isSimplifiedProject && (
				<ModalTrigger
					entryPoint={asyncCreateIssueTypeModalEntryPoint}
					entryPointParams={entryPointParams}
					entryPointProps={createEntryPointProps}
					modalProps={modalProps}
					Fallback={() => <FormDialogSkeleton numFormFields={1} />}
				>
					{({ ref }) => <ButtonItem ref={ref}>{formatMessage(messages.addIssueType)}</ButtonItem>}
				</ModalTrigger>
			)}
			{isSimplifiedProject && (
				<ModalTrigger
					entryPoint={asyncEditIssueTypeModalEntrypoint}
					entryPointParams={entryPointParams}
					entryPointProps={editEntryPointProps}
					modalProps={modalProps}
					Fallback={() => <FormDialogSkeleton numFormFields={1} />}
				>
					{({ ref }) => <ButtonItem ref={ref}>{formatMessage(messages.editIssueType)}</ButtonItem>}
				</ModalTrigger>
			)}
			<ButtonItem onClick={handleManageIssueTypesClick}>
				{formatMessage(messages.manageIssueTypes)}
			</ButtonItem>
		</Section>
	);
};
